<template>
  <!--
    Dentro do passo a passo o bloco já é a etapa: o cabeçalho repetiria o
    título que a etapa acabou de dar, e o cartão desenharia uma moldura dentro
    da outra.
  -->
  <component :is="embutido ? 'div' : 'CCard'" v-if="suportado || erro">
    <CCardHeader v-if="!embutido">
      Quando usar esta configuração
      <small class="text-muted">— uma regra para cada momento em que ela é consultada</small>
    </CCardHeader>

    <component :is="embutido ? 'div' : 'CCardBody'">
      <!--
        Erro de consulta não é ausência de regras: desligar `suportado` no
        catch fazia o bloco sumir, e com ele a única indicação de que a
        pergunta existe para este conector.
      -->
      <CAlert v-if="erro" show color="danger" class="alerta-carga">
        <span>{{ erro }}</span>
        <CButton color="danger" variant="outline" size="sm" @click="carregar">
          Tentar novamente
        </CButton>
      </CAlert>

      <div v-if="suportado" class="form-group prioridade">
        <label for="integracao-prioridade">Prioridade</label>
        <!--
          Campo apagado emitia `Number('')` → 0, e a configuração subia
          sozinha para o topo da ordem; entrada inacabada ('-', '1e') emitia
          NaN, que vira nulo no JSON e some sem erro. O último valor válido
          fica, e o piso é 1.
        -->
        <input
          id="integracao-prioridade"
          type="number"
          min="1"
          class="form-control"
          :value="prioridade"
          @input="atualizarPrioridade($event.target.value)"
        />
        <small class="campo-ajuda">
          Só ordena a lista quando mais de uma configuração atende — menor
          número aparece primeiro. Nenhuma deixa de ser consultada por causa
          dela.
        </small>
      </div>

      <!--
        Uma aba por momento. Empilhados, os blocos faziam a tela crescer sem
        fim e o usuário rolava por regras que não estava editando; a aba mostra
        de saída quais existem e abre uma de cada vez.
      -->
      <div v-if="situacoes.length > 1" class="abas">
        <button
          v-for="(situacao, indice) in situacoes"
          :key="situacao.chave"
          type="button"
          class="abas__item"
          :class="{ 'abas__item--ativa': indice === abaAtiva }"
          @click="abaAtiva = indice"
        >
          {{ situacao.nome }}
          <span class="abas__estado" :class="classeDoEstado(situacao.chave)">
            {{ estado(situacao.chave) }}
          </span>
        </button>
      </div>

      <RegraSituacao
        v-if="situacaoAtual"
        :key="situacaoAtual.chave"
        :situacao="situacaoAtual"
        :condicao="configuracaoDe(situacaoAtual.chave).condicao"
        :ativo="configuracaoDe(situacaoAtual.chave).ativo"
        :chave-rastreio="configuracaoDe(situacaoAtual.chave).chave_rastreio"
        :operadores="operadores"
        :permite-desligar="situacoes.length > 1"
        @update:configuracao="alterar(situacaoAtual.chave, $event)"
      />
    </component>
  </component>
</template>

<script>
/*
 * Regra que decide quando esta configuração é consultada.
 *
 * Vale para qualquer conector que concorra com outros pela mesma operação —
 * gateways de frete e, agora, armazéns de WMS. Por isso o texto fala de
 * "configuração" e não de "gateway": é a mesma tela nos dois casos.
 *
 * Reaproveita o construtor de condições dos Gatilhos em vez de trazer um
 * segundo editor: é a mesma pergunta ("campo, operador, valor") e o usuário já
 * monta condição assim no produto. O backend também avalia com o mesmo
 * avaliador, então o que a tela monta é exatamente o que será avaliado.
 *
 * Só o modo visual, que avalia em memória. A condição em SQL, que os Gatilhos
 * oferecem, sairia cara aqui: esta regra é avaliada durante a cotação de frete,
 * com o cliente esperando, e para cada instância configurada — várias consultas
 * ao banco do ERP por operação. Faltando informação, o caminho é acrescentar
 * campo ao contexto, e não abrir consulta livre no meio da compra.
 *
 * Só aparece para conectores que suportam roteamento — cotação e rastreio de
 * frete e acompanhamento no armazém. Os demais têm uma instância por finalidade
 * e não precisam escolher.
 */
import { get } from '@/helpers/apiRequest';
import RegraSituacao from './RegraSituacao';

export default {
  name: 'RegraRoteamento',
  components: { RegraSituacao },
  props: {
    slug: {
      type: String,
      required: true,
    },

    // No passo a passo o bloco é o conteúdo da etapa, sem cartão em volta.
    embutido: {
      type: Boolean,
      default: false,
    },
    // Mapa situação => {ativo, condicao, chave_rastreio}.
    configuracoes: {
      type: Object,
      default: () => ({}),
    },
    prioridade: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      situacoes: [],
      operadores: [],
      suportado: false,
      abaAtiva: 0,
      erro: '',
    };
  },
  computed: {
    situacaoAtual() {
      return this.situacoes[this.abaAtiva] || this.situacoes[0] || null;
    },
  },
  watch: {
    slug: 'carregar',
  },
  methods: {
    carregar() {
      if (!this.slug) {
        return;
      }

      this.erro = '';

      get(`/admin/integracoes/catalogo/${this.slug}/campos-roteamento`)
        .then((resposta) => {
          const dados = (resposta && resposta.data) || resposta || {};

          this.situacoes = dados.situacoes || [];
          this.operadores = dados.operadores || [];
          this.suportado = !!dados.suportado;
          this.erro = '';
        })
        .catch((erro) => {
          this.suportado = false;
          this.erro = (erro && erro.message)
            || 'Não foi possível carregar as situações deste conector.';
        });
    },

    atualizarPrioridade(texto) {
      const numero = parseInt(texto, 10);

      this.$emit('update:prioridade', Number.isNaN(numero) ? this.prioridade : Math.max(1, numero));
    },

    /*
     * Situação nunca configurada é atendida sem regra — o mesmo padrão do
     * backend, para a tela não mostrar um estado que o servidor não aplica.
     */
    configuracaoDe(situacao) {
      const configuracao = this.configuracoes[situacao] || {};

      return {
        ativo: configuracao.ativo !== false,
        condicao: configuracao.condicao || null,
        chave_rastreio: configuracao.chave_rastreio || null,
      };
    },

    estado(situacao) {
      const configuracao = this.configuracaoDe(situacao);

      if (!configuracao.ativo) {
        return 'não usa';
      }

      const condicao = configuracao.condicao;

      return condicao && Array.isArray(condicao.grupos) && condicao.grupos.length
        ? 'com regra'
        : 'sempre';
    },

    classeDoEstado(situacao) {
      const estado = this.estado(situacao);

      return {
        'abas__estado--regra': estado === 'com regra',
        'abas__estado--desligada': estado === 'não usa',
      };
    },

    /*
     * Cada aba cuida da sua situação; aqui só se recompõe o mapa inteiro,
     * porque é ele que o formulário envia.
     */
    alterar(situacao, configuracao) {
      this.$emit('update:configuracoes', {
        ...this.configuracoes,
        [situacao]: configuracao,
      });
    },
  },
  beforeMount() {
    this.carregar();
  },
};
</script>

<style scoped>
.alerta-carga {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

/*
 * Pílulas, e não abas com borda inferior.
 *
 * Conector que atende três momentos não cabe numa linha só na tela estreita
 * nem no formulário inteiro: as abas quebravam em duas fileiras e a borda
 * passava no meio, deixando a aba ativa solta acima da linha. A pílula se
 * repete em qualquer número de linhas sem desenhar moldura nenhuma.
 */
.abas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 20px 0 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e4e7eb;
}

.abas__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border: 1px solid #e4e7eb;
  border-radius: 999px;
  background: #fff;
  color: #667085;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.abas__item:hover {
  border-color: #c9c7dd;
  color: #3a3e81;
}

.abas__item--ativa {
  border-color: #3a3e81;
  background: #eceaf3;
  color: #3a3e81;
  font-weight: 600;
}

.abas__estado {
  padding: 1px 8px;
  border-radius: 10px;
  background: #f2f4f7;
  color: #98a2b3;
  font-size: 11px;
  font-weight: 600;
}

.abas__estado--regra {
  background: #eceaf3;
  color: #3a3e81;
}

.abas__estado--desligada {
  background: #fdecec;
  color: #a4292b;
}


/* O estreito é o campo, não o bloco: com o grupo inteiro limitado, o texto de
   ajuda quebrava em seis linhas ao lado de um palmo de espaço vazio. */
.prioridade {
  margin-bottom: 0;
}

.prioridade .form-control {
  max-width: 140px;
}

.prioridade .campo-ajuda {
  max-width: 680px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 4px;
}

.campo-ajuda {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}


</style>
